import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLang } from '../../util/lang';
import { FormControlLabel, FormLabel, Radio } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { TeethContext } from '../../components/DifferentRetainer/PrescriptionNote/hooks/useTeethContext';
import { IMPRESSION_TYPE } from '../hooks/DiffRetainerHooks/useRetainerRequestForm';
import useOAuthPlatformConnected from '../hooks/useOAuthPlatformConnected';
import useSetSingleFormDefaultValue from '../../components/DifferentRetainer/PrescriptionNote/hooks/useSetSingleFormDefaultValue';
import useSetCombieFormDefaultValue from '../../components/DifferentRetainer/PrescriptionNote/hooks/useSetCombineFormValue';
import { UpperAndLowerPrescriptionNote } from '../../components/DifferentRetainer/PrescriptionNote/UpperAndLowerPrescriptionNote';
import { BasePrescriptionNote } from '../../components/DifferentRetainer/PrescriptionNote/BasePrescriptionNote';
import { FormProvider } from 'react-hook-form';
import { getFieldName } from './DiffRetainer';
import RetainerRequestHandler from './modal/RetainerRequestHandler';
import useStoreFilesForRetainerReqest from '../hooks/DiffRetainerHooks/useStoreFilesForRetainerReqest';
import { easySnackbar } from '../../store/Noti/noti.actions';
import useHandleAddOnFields from '../hooks/DiffRetainerHooks/useHandleAddOnFields';
import {
    RESET_FORM_VALUES,
    RESET_TOOTH_SELECTIONS,
    SAVE_FORM_VALUES,
    SET_SPECIAL_INSTRUCTION,
} from '../../store/DiffRetainers/actionType';
import { BaseProps, ResubmitProps } from './type';
import _, { debounce } from 'lodash';
import { Base_Retainer_Request_Err } from '../../store/NewPatient/utils';
/**
 * @param {Object} props
 * @param {boolean} props.isNewCase
 * @param {Function} props.setLoading
 * @param {Object} props.history
 * @param {string} props.caseId
 * @param {Function} props.onProgressChange
 * @param {BaseProps} props.baseProps
 * @param {boolean} props.isTab
 * @param {boolean} props.isReSubmit
 * @param {BaseProps} props.baseProps
 * @param {boolean} props.isReSubmit
 * @param {ResubmitProps} props.resubmitProps
 */
const DiffRetainerForm = (props) => {
    const {
        isNewCase,
        setLoading,
        history,
        caseId,
        onProgressChange,
        baseProps,
        isTab,
        tabMode,
        isReSubmit,
        resubmitProps,
    } = props;
    const {
        singleRetainerTypeFormProps,
        upperAndLowerRetainerTypeFormProps,
        baseTeethSelections,
        upperAndLowerTeethSelections,
        handleFormValuesWithTeethSelections,
        resetAllTeethSelections,

        impressionType,
        setImpressionType,
        specialInstruction,
        setSpecialInstruction,
    } = baseProps;
    const diffRetainer = useSelector((state) => state.diffRetainer);
    const dispatch = useDispatch();
    const getDiffRetainerLang = useLang('DiffRetainer');
    const {
        questions,
        prescriptionFormValues,
        supportedRetainerTypes,
        toothBiteRamp,
        upperToothBiteRamp,
        lowerToothBiteRamp,
        retainerRequestId,
    } = diffRetainer;

    const [expanded, setExpanded] = useState(false);
    const { threeshapeConnected, setThreeshapeConnected } = useOAuthPlatformConnected(impressionType);

    const { storeFilesValue } = useStoreFilesForRetainerReqest();
    const { handleAddOnFields } = useHandleAddOnFields();
    const get = useLang();

    // for single retainer type
    useSetSingleFormDefaultValue(
        singleRetainerTypeFormProps.methods,
        singleRetainerTypeFormProps.formValues,
        impressionType
    );

    // for upper and lower retainer type
    useSetCombieFormDefaultValue(
        upperAndLowerRetainerTypeFormProps.methods,
        upperAndLowerRetainerTypeFormProps.formValues
    );

    const handleExpand = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    const onSubmit = async (data) => {
        try {
            setLoading(true);
            const formValues = handleFormValuesWithTeethSelections(
                data,
                { baseTeethSelections, upperAndLowerTeethSelections },
                impressionType
            );
            const addOnFields = [{ type: 'string', value: specialInstruction, field: 'specialInstruction' }];
            const proccessedFormValues = handleAddOnFields(formValues, addOnFields);
            const retainerReqHandler = new RetainerRequestHandler({
                impressionType,
                caseId: caseId ? caseId : null,
                isReSubmit,
            });
            const proccessedData = retainerReqHandler.handleRetainerRequest(proccessedFormValues, storeFilesValue);
            if (isReSubmit) {
                const res = await retainerReqHandler.resubmitRetainerRequestWithCaseId(
                    proccessedData,
                    resubmitProps.caseId,
                    resubmitProps.caseImpressionId,
                    resubmitProps.retainerRequestId,
                    onProgressChange
                );
                if (res.status === 200 && res.data && res.data.success) {
                    if (history) {
                        history.go(0);
                    } else window.location.reload();
                }
            } else {
                const res = await retainerReqHandler.createRetainerRequestWithCase(proccessedData, onProgressChange);
                if (res.status === 200 && res.data) {
                    if (res.data.success) history.push(`/patients/${caseId}`);
                }
            }
        } catch (error) {
            let message = error.message;
            if (error.response && error.response.data && error.response.data.message) {
                message = error.response.data.message;
            }
            dispatch(easySnackbar(message, 'error'));
        } finally {
            setLoading(false);
        }
    };

    const onError = (error) => {
        if (Object.keys(error).length > 0) {
            const fieldName = Object.keys(error)[0];
            switch (impressionType) {
                case IMPRESSION_TYPE.UPPER_AND_LOWER:
                    const targetExpand = fieldName.startsWith('Upper') ? 'Upper' : 'Lower';
                    setExpanded(targetExpand);
                    break;
                default:
                    setExpanded('panel1');
                    break;
            }
            const field = document.getElementById(getFieldName(fieldName));
            if (field) {
                field.scrollIntoView({ behavior: 'smooth' });
            }
        }
    };

    const impressionTypeSummary = useMemo(() => {
        if (impressionType && impressionType.length > 0) {
            return getDiffRetainerLang(`formOptions.${impressionType}`);
        }
        return '-';
    }, [impressionType, getDiffRetainerLang]);

    const resetAddOnFields = () => {
        resetAllTeethSelections();
        setSpecialInstruction('');
    };

    // handle impression type click,
    // will reset all formValues whenever user click on different impression type
    const handleImpressionClick = (e) => {
        if (e.target.value === impressionType) {
            setImpressionType('');
        } else {
            setImpressionType(e.target.value);
        }
        singleRetainerTypeFormProps.methods.reset();
        upperAndLowerRetainerTypeFormProps.methods.reset();
        if (isNewCase) {
            dispatch({ type: RESET_TOOTH_SELECTIONS });
            dispatch({ type: RESET_FORM_VALUES });
            dispatch({ type: SET_SPECIAL_INSTRUCTION, payload: '' });
            const err = {
                id: Base_Retainer_Request_Err.id,
                valid: false,
                msg: get('@NewPatient.warning.prescription_notes'),
            };
            dispatch({ type: 'NEW_PATIENT_DATA_ERROR', errorMsg: err });
        }
        resetAddOnFields();
    };

    const uiProps = {
        impressionTypeSummary,
        setImpressionType,
        impressionType,
        handleExpand,
        expanded,
        setExpanded,
        isNewCase: isNewCase,
    };

    const oauthProps = {
        threeshapeConnected,
        setThreeshapeConnected,
    };

    const addOnProps = {
        specialInstruction,
        setSpecialInstruction,
        resetAddOnFields,
    };

    const debouncedValidate = useCallback(
        debounce(async (formValue) => {
            if (!isTab) return;
            const { targetTypeRetainerValidationFunc } = diffRetainer;
            if (targetTypeRetainerValidationFunc && formValue && formValue.impressionType) {
                const isValid = await targetTypeRetainerValidationFunc();
                dispatch({
                    type: 'NEW_PATIENT_DATA_ERROR',
                    errorMsg: {
                        id: Base_Retainer_Request_Err.id,
                        valid: isValid,
                        msg: isValid ? '' : get('@NewPatient.warning.prescription_notes'),
                    },
                });
            }
        }, 900),
        [diffRetainer, isTab]
    );

    useEffect(() => {
        const saveDraft = async () => {
            if (!isTab) return;
            if (tabMode === 'retainer_request_prescriptionNotes') {
                if (!impressionType || !isTab) return;
                const targetFormValues =
                    impressionType === IMPRESSION_TYPE.UPPER_AND_LOWER
                        ? upperAndLowerRetainerTypeFormProps.formValues
                        : singleRetainerTypeFormProps.formValues;

                const formValues = handleFormValuesWithTeethSelections(
                    targetFormValues,
                    { baseTeethSelections, upperAndLowerTeethSelections },
                    impressionType
                );
                const addOnFields = [{ type: 'string', value: specialInstruction, field: 'specialInstruction' }];
                const proccessedFormValues = handleAddOnFields(formValues, addOnFields);
                const retainerReqHandler = new RetainerRequestHandler({
                    impressionType,
                    caseId: caseId ? caseId : null,
                });
                const proccessedData = retainerReqHandler.getRetainerRequestFormValues(proccessedFormValues);
                const isEqual = _.isEqual(proccessedData, prescriptionFormValues);
                if (isEqual) {
                    return;
                }
                dispatch({ type: SAVE_FORM_VALUES, payload: proccessedData });
                const payload = retainerReqHandler.getRetainerReqFormValuesForDraftCaseSummaryDisplay(
                    proccessedData,
                    retainerRequestId || null,
                    supportedRetainerTypes,
                    { toothBiteRamp, upperToothBiteRamp, lowerToothBiteRamp },
                    diffRetainer.specialInstruction
                );
                dispatch({ type: 'NEW_PATIENT_SET_RETAINER_REQUEST', payload });
                await debouncedValidate(proccessedData);
            }
        };
        saveDraft();
    }, [
        tabMode,
        impressionType,
        upperAndLowerRetainerTypeFormProps.formValues,
        singleRetainerTypeFormProps.formValues,
        caseId,
        prescriptionFormValues,
        supportedRetainerTypes,
        dispatch,
        isTab,
        toothBiteRamp,
        upperToothBiteRamp,
        lowerToothBiteRamp,
        diffRetainer.specialInstruction,
        retainerRequestId,
    ]);

    return (
        <div>
            <ol>
                <li>
                    <FormLabel>{getDiffRetainerLang(`formQuestionLabel.${questions[1]?.name}`)}</FormLabel>
                    <div>
                        {questions[1].option.map((opt) => (
                            <FormControlLabel
                                key={opt.value}
                                label={getDiffRetainerLang(`formOptions.${opt.value}`)}
                                control={
                                    <Radio
                                        name={questions[1].name}
                                        checked={impressionType === opt.value}
                                        value={opt.value}
                                        onChange={(e) => handleImpressionClick(e)}
                                    />
                                }
                            />
                        ))}
                    </div>
                </li>
            </ol>

            <TeethContext.Provider value={{ baseTeethSelections, upperAndLowerTeethSelections }}>
                {impressionType === IMPRESSION_TYPE.UPPER_AND_LOWER ? (
                    <FormProvider {...upperAndLowerRetainerTypeFormProps.methods}>
                        <UpperAndLowerPrescriptionNote
                            onSubmit={onSubmit}
                            onError={onError}
                            uiProps={uiProps}
                            formProps={{
                                errors: upperAndLowerRetainerTypeFormProps.errors,
                                formValues: upperAndLowerRetainerTypeFormProps.formValues,
                                dirtyFields: upperAndLowerRetainerTypeFormProps.dirtyFields,
                            }}
                            oauthProps={oauthProps}
                            questions={questions}
                            addOnProps={addOnProps}
                            isTab={isTab}
                            currentEditingType={expanded} // for expanded panel (current editing panel)
                            isReSubmit={isReSubmit}
                        />
                    </FormProvider>
                ) : (
                    <FormProvider {...singleRetainerTypeFormProps.methods}>
                        <BasePrescriptionNote
                            onSubmit={onSubmit}
                            onError={onError}
                            uiProps={uiProps}
                            formProps={{
                                errors: singleRetainerTypeFormProps.errors,
                                formValues: singleRetainerTypeFormProps.formValues,
                                dirtyFields: singleRetainerTypeFormProps.dirtyFields,
                            }}
                            oauthProps={oauthProps}
                            questions={questions}
                            addOnProps={addOnProps}
                            isTab={isTab}
                            isReSubmit={isReSubmit}
                        />
                    </FormProvider>
                )}
            </TeethContext.Provider>
        </div>
    );
};

export default DiffRetainerForm;
